"use client";

import Image from "next/image";
import Link from "@/components/ui/Link";
import { useState, useEffect, useLayoutEffect, useRef } from "react";

import { MdOutlineDirectionsRun } from "react-icons/md";

interface BreakingNewsItem {
  headline: string;
  slug: string | null;
}

type Props = {
  items?: BreakingNewsItem[]; // Server-prefetched breaking news items
  speedMs?: number; // full loop duration
};

// Flip animation component for Tamil text
function TamilTextFlip() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const texts = ["செய்திகள்", "உடனுக்குடன்"];

  useEffect(() => {
    // Increase interval to 5s to reduce main thread blocking
    const interval = setInterval(() => {
      setIsFlipping(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % texts.length);
        setIsFlipping(false);
      }, 250);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <span className="inline-block relative h-4 w-24" style={{ perspective: "100px" }}>
      <span
        className="absolute inset-0 flex items-center justify-center transition-transform duration-500 ease-in-out"
        style={{
          transform: `rotateX(${isFlipping ? 90 : 0}deg)`,
          transformOrigin: "center",
          // Promote to its own compositor layer so the flip animation doesn't
          // repaint each frame (Lighthouse: "Avoid non-composited animations")
          willChange: "transform",
        }}
      >
        {texts[currentIndex]}
      </span>
    </span>
  );
}

// Skeleton component for initial load
function BreakingNewsSkeleton() {

  return (
    <div className="w-full" role="region" aria-label="Breaking News Loading">
      <div>
        <div className="max-w-[1245px] mx-auto w-full flex items-stretch lg:h-[41px] h-[25px]">
          {/* Left label */}
          <div className="hidden md:flex shrink-0 items-center gap-2 px-4 text-1B1B1B text-12-english-700 bg-F2CC42">
            <MdOutlineDirectionsRun className="text-[18px] text-012061 animate-bounce" />
            <TamilTextFlip />
            <Image src="https://media.dinamani.com/dinamani/2026-02-27/71ogrt32/mick.png" alt="" width={18} height={18} aria-hidden loading="lazy" />
          </div>

          {/* Divider */}
          <div className="hidden md:block w-px self-stretch bg-DCDCDC" />

          {/* Skeleton ticker */}
          <div className="relative flex-1 overflow-hidden flex items-center bg-FFDB59">
            <div className="flex items-center gap-4 px-4 animate-pulse">
              <div className="h-3 bg-yellow-400/50 rounded w-32" />
              <div className="w-2 h-2 rounded-full bg-012061/30" />
              <div className="h-3 bg-yellow-400/50 rounded w-48" />
              <div className="w-2 h-2 rounded-full bg-012061/30" />
              <div className="h-3 bg-yellow-400/50 rounded w-40" />
              <div className="w-2 h-2 rounded-full bg-012061/30" />
              <div className="h-3 bg-yellow-400/50 rounded w-36" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Breaking News ticker bar matching the provided design
export default function BreakingNews({
  items: propItems = [],
  speedMs = 20, // pixels per second — constant slow speed regardless of content length
}: Props) {
  const [isPaused, setIsPaused] = useState(false);
  // Use server-prefetched items directly (no client fetch needed)
  const newsItems = propItems;

  const [animationDuration, setAnimationDuration] = useState<number | null>(null);
  const marqueeRef = useRef<HTMLDivElement>(null);

  const measureAndSetDuration = () => {
    if (!marqueeRef.current || newsItems.length === 0) return;
    const contentWidth = marqueeRef.current.scrollWidth / 2;
    if (contentWidth <= 0) return;
    setAnimationDuration(contentWidth / speedMs);
  };

  useLayoutEffect(() => {
    // Measure only after Tamil fonts are ready so width is final
    // and the animation doesn't restart with a new duration mid-scroll.
    document.fonts.ready.then(() => measureAndSetDuration());
  }, [newsItems, speedMs]);

  if (newsItems.length === 0) {
    return null;
  }

  const renderNewsItem = (item: BreakingNewsItem, idx: number, keyPrefix: string = "") => {
    if (item.slug) {
      return (
        <Link
          key={`${keyPrefix}-${idx}`}
          href={`/${item.slug}`}
          className="news-link inline-flex items-center gap-3 px-4 whitespace-nowrap flex-shrink-0"
        >
          <span className="inline-block w-2 h-2 rounded-full bg-012061 flex-shrink-0" />
          <span className="leading-none align-middle">{item.headline}</span>
        </Link>
      );
    }

    return (
      <span key={`${keyPrefix}-${idx}`} className="inline-flex items-center gap-3 px-4 whitespace-nowrap flex-shrink-0">
        <span className="inline-block w-2 h-2 rounded-full bg-012061 flex-shrink-0" />
        <span className="leading-none align-middle">{item.headline}</span>
      </span>
    );
  };

  return (
    <div className="w-full" role="region" aria-label="Breaking News">
      <div>
        <div className="max-w-[1245px] mx-auto w-full flex items-stretch lg:h-[40px] h-[30px]">
          {/* Left label */}
          <div className="hidden md:flex shrink-0 items-center gap-2 px-4 text-1B1B1B text-12-english-700 bg-F2CC42">
            {/* <span className="running-icon">
              <MdOutlineDirectionsRun className="text-[18px] text-012061" />
            </span> */}
            <TamilTextFlip />
            {/* <Image src="https://media.dinamani.com/dinamani/2026-02-27/71ogrt32/mick.png" alt="" width={18} height={18} aria-hidden loading="lazy" /> */}
          </div>

          {/* Divider between label and ticker */}
          <div className="hidden md:block w-px self-stretch bg-DCDCDC" />

          {/* Ticker */}
          <div
            className="relative flex-1 overflow-hidden flex items-center bg-FFDB59"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div
              ref={marqueeRef}
              className="marquee-track"
              style={{
                animationPlayState: isPaused ? 'paused' : 'running',
                animationDuration: animationDuration ? `${animationDuration}s` : '0s',
                animationName: animationDuration ? 'marquee-scroll' : 'none',
              }}
            >
              <div className="marquee-content lg:text-14-tamil-400 text-12-tamil-600 text-1B1B1B">
                {newsItems.map((item, idx) => renderNewsItem(item, idx, "a"))}
              </div>
              <div
                className="marquee-content lg:text-14-tamil-400 text-12-tamil-600 text-1B1B1B"
                aria-hidden="true"
                // `inert` removes all descendants from the tab order and a11y tree.
                // Without this, the duplicate marquee links remained focusable even
                // though aria-hidden="true" — Lighthouse flags this as
                // "[aria-hidden=true] elements contain focusable descendents".
                inert
              >
                {newsItems.map((item, idx) => renderNewsItem(item, idx, "b"))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee CSS is in globals.css to prevent FOUC before JS hydrates */}
    </div>
  );
}

